/**
 * Unit categories for context-aware styling and processing
 */
export var UnitCategory;
(function (UnitCategory) {
    UnitCategory["VOLUME"] = "volume";
    UnitCategory["WEIGHT"] = "weight";
    UnitCategory["SMALL_QUANTITY"] = "small_quantity";
    UnitCategory["COUNTABLE"] = "countable";
    UnitCategory["LENGTH"] = "length";
})(UnitCategory || (UnitCategory = {}));
/**
 * Shared list of ingredient unit keywords for highlighting
 * Used by both web-spa and mobile apps for consistent ingredient highlighting
 * Version 3: Organized by category for context-aware processing
 */
export const ingredientUnitsByCategory = {
    [UnitCategory.VOLUME]: [
        "cup",
        "cups",
        "tbsp",
        "tablespoon",
        "tablespoons",
        "tsp",
        "teaspoon",
        "teaspoons",
        "fl oz",
        "fluid ounce",
        "fluid ounces",
        "pt",
        "pint",
        "pints",
        "qt",
        "quart",
        "quarts",
        "gal",
        "gallon",
        "gallons",
        "ml",
        "milliliter",
        "milliliters",
        "l",
        "liter",
        "liters",
    ],
    [UnitCategory.WEIGHT]: [
        "oz",
        "ounce",
        "ounces",
        "lb",
        "pound",
        "pounds",
        "g",
        "gram",
        "grams",
        "kg",
        "kilogram",
        "kilograms",
    ],
    [UnitCategory.SMALL_QUANTITY]: [
        "dash",
        "dashes",
        "pinch",
        "pinches",
        "drop",
        "drops",
        "splash",
        "splashes",
    ],
    [UnitCategory.COUNTABLE]: [
        "clove",
        "cloves",
        "can",
        "cans",
        "package",
        "packages",
        "bunch",
        "bunches",
        "head",
        "heads",
        "piece",
        "pieces",
        "slice",
        "slices",
        "whole",
        "halves",
        "half",
        "stalk",
        "stalks",
        "leaf",
        "leaves",
        "strip",
        "strips",
    ],
    [UnitCategory.LENGTH]: [
        "inch",
        "inches",
        "in",
        "cm",
        "centimeter",
        "centimeters",
    ],
};
/**
 * Flat list of all units (backward compatibility)
 */
export const ingredientUnits = Object.values(ingredientUnitsByCategory).flat();
/**
 * Map of unit to category for quick lookups
 */
export const unitToCategoryMap = new Map(Object.entries(ingredientUnitsByCategory).flatMap(([category, units]) => units.map((unit) => [unit.toLowerCase(), category])));
/**
 * Generates a regex pattern string for matching ingredient units
 * Escapes special regex characters and creates word boundaries
 * Version 3: Cached pattern for performance, sorted by length for accuracy
 */
let cachedUnitPattern = null;
export function generateUnitPattern() {
    if (cachedUnitPattern !== null) {
        return cachedUnitPattern;
    }
    // Escape special regex characters in each unit and join with |
    // Sort by length (longest first) to prevent shorter matches from overriding longer ones
    // (e.g., "fl oz" should match before "oz")
    const sortedUnits = [...ingredientUnits].sort((a, b) => b.length - a.length);
    const escapedUnits = sortedUnits.map((unit) => unit.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    cachedUnitPattern = `\\b(${escapedUnits.join("|")})\\b`;
    return cachedUnitPattern;
}
/**
 * Get the category for a unit (case-insensitive)
 */
export function getUnitCategory(unit) {
    return unitToCategoryMap.get(unit.toLowerCase());
}
